import { useState, useEffect, useCallback, useRef } from 'react';
import { saveToStorage, loadFromStorage, removeFromStorage, STORAGE_KEYS, getTimerDefaultSettings } from '../utils/localStorage';
import { playAlarmSound, unlockAudio } from '../utils/audioUtils';

export function useSingleTimer() {
  // 定时器设置状态
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(5);
  const [seconds, setSeconds] = useState(0);
  const [volume, setVolume] = useState(70);
  const [growing, setGrowing] = useState(false);
  const [selectedMelody, setSelectedMelody] = useState('BELL');
  
  // 视频闹钟设置
  const [useVideoAlarm, setUseVideoAlarm] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [videoId, setVideoId] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  // 定时器运行状态
  const [isRunning, setIsRunning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isRinging, setIsRinging] = useState(false);

  // YouTube播放器状态
  const [ytApiReady, setYtApiReady] = useState(false);
  const [showPlayPrompt, setShowPlayPrompt] = useState(false);
  const playerRef = useRef(null);
  const playerContainerIdRef = useRef(null);
  const playerMountIdRef = useRef(null);

  const intervalRef = useRef(null);
  const isInitialMount = useRef(true);
  const testAudioRef = useRef(null);

  // 音频解锁函数
  const handleUnlockAudio = useCallback(async () => {
    try {
      await unlockAudio();
      setAudioUnlocked(true);
      console.log('Timer audio unlocked successfully');
    } catch (error) {
      console.error('Failed to unlock timer audio:', error);
    }
  }, []);

  // 提取视频ID
  const extractVideoId = useCallback((url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  }, []);

  // 处理YouTube URL变化
  const handleUrlChange = useCallback((url) => {
    setYoutubeUrl(url);
    const id = extractVideoId(url);
    if (id) {
      setVideoId(id);
      if (!audioUnlocked) {
        handleUnlockAudio();
      }
    } else {
      setVideoId('');
    }
  }, [extractVideoId, audioUnlocked, handleUnlockAudio]);

  // 创建YouTube播放器
  const createYouTubePlayer = useCallback((container, options = {}) => {
    const { allowFullscreen = false, autoplay = undefined } = options;
    
    if (!videoId) {
      console.warn('createYouTubePlayer called without videoId');
      return;
    }

    let targetElement = null;
    let containerId = null;

    if (typeof container === 'string') {
      containerId = container;
      targetElement = document.getElementById(container);
    } else if (container && typeof container === 'object' && 'nodeType' in container) {
      targetElement = container;
      containerId = container.id;
      if (!containerId) {
        containerId = `youtube-player-${Date.now()}`;
        container.id = containerId;
      }
    } else {
      containerId = playerContainerIdRef.current || 'timer-video-player';
      targetElement = document.getElementById(containerId);
    }

    if (!targetElement) {
      console.warn('YouTube player element not found, retrying...');
      setTimeout(() => {
        const retryElement = document.getElementById(containerId);
        if (retryElement) {
          console.log('YouTube player element found on retry');
          createYouTubePlayer(containerId, options);
        } else {
          console.error('YouTube player element still not found after retry');
          setShowPlayPrompt(true);
        }
      }, 500);
      return;
    }

    playerContainerIdRef.current = containerId;

    if (!window.YT || !window.YT.Player) {
      console.error('YouTube API not available');
      setShowPlayPrompt(true);
      return;
    }

    if (playerRef.current) {
      try {
        console.log('Destroying previous YouTube player');
        if (typeof playerRef.current.destroy === 'function') {
          playerRef.current.destroy();
        }
      } catch (error) {
        console.error('Error destroying previous player:', error);
      } finally {
        playerRef.current = null;
      }
    }

    // 清空内部内容，创建专用挂载节点
    while (targetElement.firstChild) {
      try {
        targetElement.removeChild(targetElement.firstChild);
      } catch (error) {
        console.warn('Error removing child node during cleanup:', error);
        break;
      }
    }

    const mountElement = document.createElement('div');
    const mountId = `${targetElement.id}-mount`;
    mountElement.id = mountId;
    mountElement.setAttribute('data-yt-player-mount', 'true');
    targetElement.appendChild(mountElement);
    playerMountIdRef.current = mountId;

    try {
      console.log('Creating YouTube player for timer in container:', containerId);
      playerRef.current = new window.YT.Player(mountElement, {
        height: '450',
        width: '100%',
        videoId: videoId,
        playerVars: {
          autoplay: typeof autoplay === 'number' ? autoplay : (isRinging ? 1 : 0),
          controls: 1,
          enablejsapi: 1,
          origin: window.location.origin,
          rel: 0,
          showinfo: 0,
          modestbranding: 1,
          fs: allowFullscreen ? 1 : 0,
          cc_load_policy: 0,
          iv_load_policy: 3,
          autohide: 1
        },
        events: {
          onReady: (event) => {
            console.log('Timer YouTube player ready');
            setYtApiReady(true);
            if (isRinging) {
              try {
                event.target.setVolume(volume);
                event.target.playVideo();
                console.log('Timer video started playing automatically');
              } catch (error) {
                console.error('Error auto-playing timer video:', error);
                setShowPlayPrompt(true);
              }
            }
          },
          onStateChange: (event) => {
            console.log('Timer YouTube player state changed:', event.data);
            if (event.data === window.YT.PlayerState.ENDED && isRinging) {
              // 视频结束后重新播放
              try {
                event.target.seekTo(0);
                event.target.playVideo();
              } catch (error) {
                console.error('Error restarting timer video:', error);
              }
            }
          },
          onError: (event) => {
            console.error('Timer YouTube player error:', event.data);
            setShowPlayPrompt(true);
          }
        }
      });
    } catch (error) {
      console.error('Error creating timer YouTube player:', error);
      setShowPlayPrompt(true);
    }
  }, [videoId, volume, isRinging]);

  // 手动开始播放视频
  const manualStartPlay = useCallback((container) => {
    console.log('Manual start play for timer');
    setShowPlayPrompt(false);
    
    let nextContainer;
    if (typeof container === 'string') {
      nextContainer = container;
    } else if (container && typeof container === 'object' && 'nodeType' in container) {
      const id = container.id || playerContainerIdRef.current || `youtube-player-timer-${Date.now()}`;
      if (!container.id) container.id = id;
      playerContainerIdRef.current = id;
      nextContainer = container;
    } else if (playerContainerIdRef.current) {
      nextContainer = playerContainerIdRef.current;
    } else {
      nextContainer = 'timer-video-player';
      playerContainerIdRef.current = nextContainer;
    }
    
    if (playerRef.current && typeof playerRef.current.playVideo === 'function') {
      try {
        playerRef.current.setVolume(volume);
        playerRef.current.playVideo();
        console.log('Timer manual play started successfully');
      } catch (error) {
        console.error('Error in timer manual play:', error);
        setTimeout(() => {
          createYouTubePlayer(nextContainer, { allowFullscreen: true, autoplay: 1 });
        }, 1000);
      }
    } else {
      console.log('Timer player not ready, recreating...');
      createYouTubePlayer(nextContainer, { allowFullscreen: true, autoplay: 1 });
    }
  }, [volume, createYouTubePlayer]);

  // 停止视频播放
  const stopVideo = useCallback(() => {
    console.log('Stopping timer video playback');
    setShowPlayPrompt(false);
    
    if (playerRef.current) {
      try {
        console.log('Stopping and destroying timer YouTube player');
        if (typeof playerRef.current.stopVideo === 'function') {
          playerRef.current.stopVideo();
        }
        if (typeof playerRef.current.destroy === 'function') {
          playerRef.current.destroy();
        }
        playerRef.current = null;
      } catch (error) {
        console.error('Error stopping timer video:', error);
      }
    }
    
    // 清理播放器容器
    if (playerMountIdRef.current) {
      const mountElement = document.getElementById(playerMountIdRef.current);
      if (mountElement && mountElement.parentNode) {
        try {
          mountElement.parentNode.removeChild(mountElement);
        } catch (error) {
          console.warn('Error removing timer player mount element:', error);
        }
      }
      playerMountIdRef.current = null;
    }
    
      setYtApiReady(false);
  }, []);

  // 加载 YouTube IFrame API
  useEffect(() => {
    const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');

    if (!window.YT && !existingScript) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.id = 'youtube-api-script';

      const firstScriptTag = document.getElementsByTagName('script')[0];
      if (firstScriptTag && firstScriptTag.parentNode) {
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      } else {
        document.head.appendChild(tag);
      }

      if (!window.onYouTubeIframeAPIReady) {
        window.onYouTubeIframeAPIReady = () => {
          setYtApiReady(true);
        };
      }
    } else if (window.YT && window.YT.Player) {
      setYtApiReady(true);
    } else if (existingScript && !window.onYouTubeIframeAPIReady) {
      window.onYouTubeIframeAPIReady = () => {
        setYtApiReady(true);
      };
    }
  }, []);

  // 测试音效
  const testSound = useCallback(async () => {
    try {
      // 先解锁音频
      if (!audioUnlocked) {
        await handleUnlockAudio();
      }
      
      // 停止之前的测试音效
      if (testAudioRef.current) {
        testAudioRef.current.stop();
        testAudioRef.current = null;
      }

      // 播放测试音效
      const audioControl = await playAlarmSound(selectedMelody, volume / 100, growing);
      testAudioRef.current = audioControl;
      
      // 3秒后自动停止测试音效
      setTimeout(() => {
        if (testAudioRef.current) {
          testAudioRef.current.stop();
          testAudioRef.current = null;
        }
      }, 3000);
    } catch (error) {
      console.error('测试音效播放失败:', error);
    }
  }, [selectedMelody, volume, growing, audioUnlocked, handleUnlockAudio]);

  // 保存定时器设置到localStorage
  const saveTimerSettings = useCallback(() => {
    const settings = {
      hours,
      minutes,
      seconds,
      volume,
      growing,
      selectedMelody,
      useVideoAlarm,
      videoId,
      videoTitle,
      // 添加时间戳以便跟踪最后更新时间
      lastUpdated: Date.now()
    };
    saveToStorage(STORAGE_KEYS.TIMER_SETTINGS, settings);
  }, [hours, minutes, seconds, volume, growing, selectedMelody, useVideoAlarm, videoId, videoTitle]);

  // 保存定时器激活状态到localStorage
  const saveTimerActiveState = useCallback((active, remaining, startTime, totalTime, paused) => {
    const state = {
      active,
      remaining,
      startTime,
      totalTime,
      paused,
      // 添加保存时间戳
      savedAt: Date.now()
    };
    saveToStorage(STORAGE_KEYS.TIMER_ACTIVE_STATE, state);
  }, []);

  // 从localStorage加载设置
  useEffect(() => {
    try {
      // 获取默认设置
      const defaultSettings = getTimerDefaultSettings();
      
      // 加载定时器设置
      const savedSettings = loadFromStorage(STORAGE_KEYS.TIMER_SETTINGS, defaultSettings);
      
      // 使用默认值确保所有设置都有值
      setHours(savedSettings.hours ?? defaultSettings.hours);
      setMinutes(savedSettings.minutes ?? defaultSettings.minutes);
      setSeconds(savedSettings.seconds ?? defaultSettings.seconds);
      setVolume(savedSettings.volume ?? defaultSettings.volume);
      setGrowing(savedSettings.growing ?? defaultSettings.growing);
      setSelectedMelody(savedSettings.selectedMelody ?? defaultSettings.selectedMelody);
      setUseVideoAlarm(savedSettings.useVideoAlarm ?? defaultSettings.useVideoAlarm);
      setVideoId(savedSettings.videoId ?? defaultSettings.videoId);
      setVideoTitle(savedSettings.videoTitle ?? defaultSettings.videoTitle);
      
      if (savedSettings !== defaultSettings) {
        console.log('✅ 定时器设置已恢复:', savedSettings);
      } else {
        console.log('📝 使用默认定时器设置');
      }

      // 恢复定时器激活状态
      const savedState = loadFromStorage(STORAGE_KEYS.TIMER_ACTIVE_STATE);
      if (savedState && savedState.active) {
        const now = Date.now();
        
        // 检查保存的状态是否有效（不超过24小时）
        const maxAge = 24 * 60 * 60 * 1000; // 24小时
        const stateAge = now - (savedState.savedAt || savedState.startTime || 0);
        
        if (stateAge > maxAge) {
          console.log('⚠️ 定时器状态过期，清除状态');
          removeFromStorage(STORAGE_KEYS.TIMER_ACTIVE_STATE);
        } else {
          const elapsed = Math.floor((now - savedState.startTime) / 1000);
          const remaining = Math.max(0, savedState.remaining - elapsed);

          if (remaining > 0) {
            setTimeLeft(remaining);
            setIsRunning(!savedState.paused);
            setIsPaused(savedState.paused);
            console.log('✅ 定时器状态已恢复:', {
              remaining,
              paused: savedState.paused,
              elapsed
            });
          } else {
            // 时间已到，触发闹钟
            setTimeLeft(0);
            setIsRunning(false);
            setIsPaused(false);
            setIsRinging(true);
            console.log('⏰ 定时器时间已到，触发闹钟');
            // 清除过期状态
            removeFromStorage(STORAGE_KEYS.TIMER_ACTIVE_STATE);
          }
        }
      } else {
        console.log('📝 无活跃的定时器状态');
      }
    } catch (error) {
      console.error('❌ 恢复定时器状态时出错:', error);
      // 出错时使用默认设置
      const defaultSettings = getTimerDefaultSettings();
      setHours(defaultSettings.hours);
      setMinutes(defaultSettings.minutes);
      setSeconds(defaultSettings.seconds);
      setVolume(defaultSettings.volume);
      setGrowing(defaultSettings.growing);
      setSelectedMelody(defaultSettings.selectedMelody);
      setUseVideoAlarm(defaultSettings.useVideoAlarm);
      setVideoId(defaultSettings.videoId);
      setVideoTitle(defaultSettings.videoTitle);
    }

    isInitialMount.current = false;
  }, []);

  // 保存设置到localStorage
  useEffect(() => {
    if (!isInitialMount.current) {
      saveTimerSettings();
    }
  }, [saveTimerSettings]);

  // 定时器倒计时逻辑
  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      let saveCounter = 0; // 用于控制保存频率
      
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          const newTimeLeft = prev - 1;
          saveCounter++;
          
          // 每10秒保存一次状态，或者在最后10秒每秒保存
          if (saveCounter % 10 === 0 || newTimeLeft <= 10) {
            const totalTime = hours * 3600 + minutes * 60 + seconds;
            saveTimerActiveState(true, newTimeLeft, Date.now() - (totalTime - newTimeLeft) * 1000, totalTime, false);
          }
          
          if (newTimeLeft <= 0) {
            setIsRunning(false);
            setIsPaused(false);
            setIsRinging(true);
            saveTimerActiveState(false, 0, null, 0, false);
            return 0;
          }
          return newTimeLeft;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRunning, timeLeft, saveTimerActiveState, hours, minutes, seconds]);

  // 监听isRinging状态变化，处理视频播放
  useEffect(() => {
    if (isRinging && useVideoAlarm && videoId) {
      console.log('Timer ringing with video alarm, creating YouTube player');
      // 延迟创建播放器，确保DOM已渲染
      setTimeout(() => {
        createYouTubePlayer('timer-video-player', { allowFullscreen: true, autoplay: 1 });
      }, 100);
    } else if (!isRinging) {
      // 停止视频播放
      stopVideo();
    }
  }, [isRinging, useVideoAlarm, videoId, createYouTubePlayer, stopVideo]);

  // 监听时间设置变化，重置定时器状态（仅当用户修改时间设置时）
  useEffect(() => {
    // 跳过初始化时的重置
    if (isInitialMount.current) return;
    
    // 如果定时器正在运行或暂停，用户修改时间设置时重置状态
    if (isRunning || isPaused) {
      resetTimer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours, minutes, seconds]);

  // 开始定时器
  const startTimer = useCallback(() => {
    const totalSeconds = hours * 3600 + minutes * 60 + seconds;
    if (totalSeconds > 0) {
      setTimeLeft(totalSeconds);
      setIsRunning(true);
      setIsPaused(false);
      setIsRinging(false);
      saveTimerActiveState(true, totalSeconds, Date.now(), totalSeconds, false);
    }
  }, [hours, minutes, seconds, saveTimerActiveState]);

  // 暂停/恢复定时器
  const pauseTimer = useCallback(() => {
    if (isRunning) {
      // 暂停
      setIsRunning(false);
      setIsPaused(true);
      saveTimerActiveState(false, timeLeft, null, 0, true);
    } else if (isPaused) {
      // 恢复
      if (timeLeft > 0) {
        setIsRunning(true);
        setIsPaused(false);
        saveTimerActiveState(true, timeLeft, Date.now(), timeLeft, false);
      }
    }
  }, [isRunning, isPaused, timeLeft, saveTimerActiveState]);

  // 重置定时器
  const resetTimer = useCallback(() => {
    setIsRunning(false);
    setIsPaused(false);
    setTimeLeft(0);
    setIsRinging(false);
    saveTimerActiveState(false, 0, null, 0, false);
  }, [saveTimerActiveState]);

  // 停止定时器（用于停止闹钟）
  const stopTimer = useCallback(() => {
    setIsRinging(false);
    resetTimer();
  }, [resetTimer]);

  return {
    // 设置状态
    hours,
    minutes,
    seconds,
    volume,
    growing,
    selectedMelody,
    useVideoAlarm,
    youtubeUrl,
    videoId,
    videoTitle,
    setHours,
    setMinutes,
    setSeconds,
    setVolume,
    setGrowing,
    setSelectedMelody,
    setUseVideoAlarm,
    setYoutubeUrl,
    setVideoId,
    setVideoTitle,
    
    // 运行状态
    isRunning,
    timeLeft,
    isPaused,
    isRinging,
    
    // YouTube播放器状态
    ytApiReady,
    showPlayPrompt,
    
    // 音频解锁状态
    audioUnlocked,
    handleUnlockAudio,
    
    // 控制函数
    startTimer,
    pauseTimer,
    resetTimer,
    stopTimer,
    testSound,
    
    // YouTube播放器控制函数
    createYouTubePlayer,
    manualStartPlay,
    stopVideo,
    
    // URL处理函数
    extractVideoId,
    handleUrlChange
  };
}